import * as React from 'react';
import {
  ICycle,
  IPercentageSplit,
  isVariantControlGroup,
  IVisitorMessageType,
  segmentHasSplitTest,
} from '../../../entities/offsiteCampaign';
import {
  useTheme,
  SelectMini,
  ISelectOption,
  Box,
  ISelectMiniOption,
  Tooltip,
  IconButton,
  Input,
  Button,
} from '@salecycle/ui-component-library';
import styled from 'styled-components';
import { Cycle } from '../editor/segment-item/Cycle';
import { State as CampaignState } from 'entities/lifecycle/lifeCycle';
import { useTranslation } from 'react-i18next';
import { uuid } from 'utils';
import {
  useHasSplitTestEnded,
  useOffsiteCampaignEditorDispatch,
  useHasSplitTestStarted,
} from '../../context/UseOffsiteCampaignEditorContext';
import { IconLabel } from 'components/experimental/IconLabel';
import { useFeatureFlags } from 'core/app/AppContext';
import SegmentItemBadge from '../common/SegmentItemBadge';

const StyledVerticalSpacing = styled.div`
  padding-top: 2rem;
`;

export interface ISplitTestListItemProps {
  split: IPercentageSplit;
  splitsLength: number;
  cloneSplitTest: (splitId: string) => void;
  deleteSplitTest: (splitId: string) => void;
  segmentId: string;
  campaignState: CampaignState;
  newCycleOptions: ISelectMiniOption[];
  sessionTime: number;
  splits: IPercentageSplit[];
  onPercentageChange: (splitId: string, newPercentage: number) => void;
}

export const SplitTestListItem: React.FC<ISplitTestListItemProps> = props => {
  const { t } = useTranslation('campaigns');
  const theme = useTheme();
  const dispatch = useOffsiteCampaignEditorDispatch();
  const iconLabelColor = theme.vars.newColor.darkGS2;
  const areSplitTestsFinished = useHasSplitTestEnded();
  const hasSplitTestsStarted = useHasSplitTestStarted();
  const doesSegmentHaveSplitTest = segmentHasSplitTest;
  const { flagEnabled } = useFeatureFlags();
  const featureSplitTest = flagEnabled('ld-split-test-scheduling');
  const featureControlGroup = flagEnabled('ld-control-group');

  const onAddCycleSelect = (option: ISelectOption<string>): void => {
    const newCycle: ICycle = {
      templateId: null,
      messageType: option.value as IVisitorMessageType,
      delay: props.sessionTime,
      id: uuid(),
      subjectLineId: undefined,
    };
    dispatch({
      type: 'ADD_CYCLE',
      payload: {
        cycle: newCycle,
        segmentId: props.segmentId,
        splitId: props.split.id,
      },
    });
  };

  const onSplitPercentageChange = (value: string) => {
    dispatch({
      type: 'UPDATE_SPLIT_PERCENTAGE',
      payload: {
        segmentId: props.segmentId,
        splitId: props.split.id,
        percentage: Number(value),
      },
    });
    const numericValue = Number(value);
    props.onPercentageChange(props.split.id, numericValue);
  };

  const hasFinished = () => {
    return areSplitTestsFinished && featureSplitTest;
  };

  const hasStarted = () => {
    return hasSplitTestsStarted && featureSplitTest;
  };

  const displayBadge = () => {
    const isFirst = props?.splits[0].name === props?.split.name;
    const isMultiple = props.splitsLength > 1;
    const defaultBadge = isMultiple && featureSplitTest && (
      <SegmentItemBadge
        color={theme.designToken.primary300}
        tooltipColor={theme.designToken.primary700}
        dataTestID="DefaultBadge"
        tooltip={t('campaigns:offsiteEditor.segments.splitTests.defaultBadge.tooltip')}
      >
        {t('campaigns:offsiteEditor.segments.splitTests.defaultBadge.label')}
      </SegmentItemBadge>
    );

    const finishedBadge = isMultiple && featureSplitTest && (
      <SegmentItemBadge color={theme.vars.newColor.purple3} dataTestID="FinishedBadge">
        {t('campaigns:offsiteEditor.segments.splitTests.finishedBadge.label')}
      </SegmentItemBadge>
    );

    const liveBadge = isMultiple && featureSplitTest && (
      <SegmentItemBadge color={theme.vars.newColor.green2} dataTestID="LiveBadge">
        {t('campaigns:offsiteEditor.segments.splitTests.liveBadge.label')}
      </SegmentItemBadge>
    );

    const scheduledBadge = isMultiple && featureSplitTest && (
      <SegmentItemBadge color={theme.vars.newColor.orange1} dataTestID="ScheduledBadge">
        {t('campaigns:offsiteEditor.segments.splitTests.scheduledBadge.label')}
      </SegmentItemBadge>
    );

    return isFirst
      ? defaultBadge
      : hasFinished()
      ? finishedBadge
      : hasStarted()
      ? liveBadge
      : !hasStarted() && !hasFinished()
      ? scheduledBadge
      : null;
  };

  return (
    <div data-testid={`${props.split.name}`} key={`${props.split.id}-split-${props.split.id}`}>
      <div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ display: 'inline-block', flexGrow: 1 }}>
            {!(
              isVariantControlGroup(props.split) &&
              doesSegmentHaveSplitTest(props.splitsLength) &&
              featureControlGroup
            ) && (
              <Button variation="secondary" type="button" onClick={() => props.cloneSplitTest(props.split.id)}>
                {t('campaigns:offsiteEditor.segments.splitTests.cloneSplitTest.label')}
              </Button>
            )}
          </div>
          {displayBadge()}
        </div>
        <div style={{ display: 'flex', flexDirection: 'row-reverse' }}>
          {isVariantControlGroup(props.split) && doesSegmentHaveSplitTest(props.splitsLength) && featureControlGroup && (
            <SegmentItemBadge
              color={theme.designToken.neutral700}
              dataTestID="ControlGroupBadge"
              tooltipColor={theme.designToken.primary700}
              tooltip={t('campaigns:offsiteEditor.segments.splitTests.controlGroupBadge.tooltip')}
            >
              {t('campaigns:offsiteEditor.segments.splitTests.controlGroupBadge.label')}
            </SegmentItemBadge>
          )}
        </div>
      </div>
      {props.splitsLength > 1 && (
        <StyledVerticalSpacing>
          <p>{props.split.name}</p>
        </StyledVerticalSpacing>
      )}
      <Input
        ariaLabel={t('campaigns:offsiteEditor.segments.splitTests.splitPercentage.percentageLabel')}
        label={t('campaigns:offsiteEditor.segments.splitTests.splitPercentage.percentageLabel')}
        inputId="PercentageSplit"
        type="number"
        inputTestId="PercentageSplitInputSelector"
        required={true}
        value={props.split.percentage}
        onChange={onSplitPercentageChange}
        min="0"
        max="100"
        placeholder={t('campaigns:offsiteEditor.segments.splitTests.splitPercentage.percentagePlaceholder')}
        isMini={true}
      />
      <IconLabel
        icon="arrow-down"
        color={iconLabelColor}
        text={t('campaigns:offsiteEditor.segments.guides.whatMessageAndWhen')}
      ></IconLabel>
      <div data-testid="CycleSelector">
        <Box stackItems={true} padding="10px 0">
          <SelectMini
            ariaLabel={t('campaigns:offsiteEditor.segments.cycles.addNewCycle.label')}
            value={{ value: null, label: t('campaigns:offsiteEditor.segments.cycles.addNewCycle.label') }}
            options={props.newCycleOptions}
            onChange={onAddCycleSelect}
          />
        </Box>
      </div>
      <StyledVerticalSpacing data-testid="SortedCycles">
        {props.split.cycles.map(
          (cycle, idx): JSX.Element => (
            <Cycle
              cycle={cycle}
              key={`${props.segmentId}-cycle-${idx}`}
              cycleIndex={idx}
              segmentId={props.segmentId}
              splitId={props.split.id}
              campaignState={props.campaignState}
            />
          ),
        )}
      </StyledVerticalSpacing>
      {props.splitsLength > 1 && (
        <Tooltip title={t('campaigns:offsiteEditor.segments.splitTests.delete.iconAriaLabel')} position="top">
          <IconButton
            ariaLabel={t('campaigns:offsiteEditor.segments.splitTests.delete.iconAriaLabel')}
            icon="trash"
            onClick={() => props.deleteSplitTest(props.split.id)}
            data-testid="DeleteSplitTestButton"
          />
        </Tooltip>
      )}
    </div>
  );
};
